<template>
  <v-container class="d-flex cards-group-container flex-wrap" data-testid="statechartCardsGroup.container">
    <div v-for="(statechart, index) in statechartsWithNew" :key="statechart.modelId">
      <v-card
        v-if="'model' in statechart"
        :color="'contrast-grey-medium'"
        class="statechart-card"
height="220"
        width="200"
        @mouseover="hoverStates[index] = true"
        @mouseleave="hoverStates[index] = false"
      >
        <v-card-text class="d-flex flex-column pl-1 pr-3 py-3">
          <v-row no-gutters>
            <v-col v-if="withNew" cols="2" class="d-flex justify-end align-center">
              <v-icon
                v-if="(hoverStates[index] || selectedIds.has(statechart.modelId)) && statechart.modelId !== activeModelId"
                size="20px"
                color="primary"
                style="cursor: pointer"
                @click.stop="toggleSelection(statechart.modelId)"
              >{{ selectedIds.has(statechart.modelId) ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}</v-icon>
              <v-icon v-else size="20px" color="contrast-grey-high">mdi-cloud-outline</v-icon>
            </v-col>
            <v-col :cols="withNew ? '10' : '12'" class="pl-2">
              <v-tooltip location="top">
                <template #activator="{ props }">
                  <div class="text-truncate" v-bind="props">
                    {{ statechart.model.title || "No Title" }}
                  </div>
                </template>
                <span>{{ statechart.model.title || "No Title" }}</span>
              </v-tooltip></v-col
            >
          </v-row>
          <v-row v-if="withNew && hoverStates[index]" no-gutters
            ><v-col cols="2"> </v-col
            ><v-col cols="10" class="pl-2">
              <div class="font-weight-light">
                Modified on
                {{ statechart.model.modifiedAt?.toLocaleDateString("EN") || "-" }}
              </div></v-col
            ></v-row
          >
          <div v-if="withNew && hoverStates[index]" class="d-flex justify-space-around pt-2">
            <v-tooltip v-for="(item, i) in menuActionsFor(statechart.modelId)" :key="i" location="bottom">
              <template #activator="{ props }">
                <v-btn-icon
                  :icon="item.icon"
                  :data-testid="item.testId"
                  v-bind="props"
                  size="40px"
                  @click="
                    () => {
                      selectedStatechart = statechart;
                      item.action(statechart?.modelId);
                    }
                  "
                ></v-btn-icon
              ></template>
              <span>{{ item.title }}</span>
            </v-tooltip>
          </div>
        </v-card-text>

        <v-card
          height="180"
          :rounded="false"
          class="d-flex justify-end"
          data-testid="statechartCardsGroup.open-card"
          @click="openStatechart(statechart)"
        >
          <v-tooltip location="bottom">
            <template #activator="{ props }">
              <v-chip
                v-if="playerState(statechart)"
                v-bind="props"
                class="ma-2"
                variant="elevated"
                :color="playerState(statechart) === 'outdated' ? 'warning' : 'success'"
                ><v-icon>mdi-share-variant</v-icon></v-chip
              >
            </template>
            <span>published player {{ playerState(statechart) }}</span>
          </v-tooltip>

          <template #image>
            <v-img v-if="statechart.model.svgUrl" class="statechartImg" :src="statechart.model.svgUrl"></v-img>
            <v-sheet v-else width="200" color="white" class="d-flex align-center justify-center">
              <v-icon style="color: rgba(var(--v-theme-contrast-grey-high))" size="80">mdi-sitemap </v-icon>
            </v-sheet>
          </template>
        </v-card>
      </v-card>

      <v-card
        v-else
        height="220"
        width="200"
        color="primary"
        data-testid="statechartCardsGroup.new-card"
        @click="$emit('openStatechart', statechart)"
      >
        <v-card-text class="text-center text-truncate px-2 py-3">New Statechart</v-card-text>
        <v-sheet height="180" color="rgba(var(--v-theme-primary))" class="d-flex align-center justify-center"
          ><v-icon style="color: rgba(var(--v-theme-white))" size="80">mdi-plus</v-icon>
        </v-sheet>
      </v-card>
    </div>
  </v-container>
  <publish-player-dialog
    v-if="openPlayer && selectedStatechart?.modelId"
    :statechart-name="selectedStatechart?.model.title || ''"
    :model-id="selectedStatechart?.modelId"
    @close="
      () => {
        emit('update:reloadModels');
        openPlayer = false;
      }
    "
  ></publish-player-dialog>
  <statechart-delete-dialog
    v-if="openDelete && selectedStatechart"
    :statechart-model-id="selectedStatechart.modelId"
    :statechart-name="selectedStatechart.model.title"
    :batch-count="pendingBatchDeleteIds.length > 0 ? pendingBatchDeleteIds.length + 1 : undefined"
    @cancel="openDelete = false"
    @deleted="statechartDeleted"
  ></statechart-delete-dialog>
  <statechart-properties-dialog
    v-if="openProperties && selectedStatechart"
    :mode="editDialogMode"
    :new-model="selectedStatechart.model"
    @cancel="
      () => {
        selectedStatechart = null;
        openProperties = false;
      }
    "
    @save="
      (arg) => {
        editDialogMode === 'edit' ? saveEdit(arg) : duplicateStatechart(arg);
      }
    "
  ></statechart-properties-dialog>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, ref, watch } from "vue";
import JSZip from "jszip";
import { newStatechart, type NewStatechart } from "./utils/NewStatechart";
import { StatechartsService } from "../services/statecharts/statecharts-service";
import PublishPlayerDialog from "../FileToolbar/PublishPlayerItem/PublishPlayerDialog.vue";
import StatechartDeleteDialog from "./StatechartDeleteDialog.vue";
import StatechartPropertiesDialog from "./StatechartPropertiesDialog.vue";
import { RecentFile } from "../services/storage/recent-items-service";
import { MessageEventBus, WizardEventBus } from "../../event-bus";
import { editorStateStore } from "../services/storage/editor-state-store";

const compProps = defineProps<{
  statecharts: RecentFile[];
  withNew: boolean;
}>();

const cloudStatechartService: StatechartsService = inject<StatechartsService>("cloudStatechartService");

const emit = defineEmits(["openStatechart", "update:reloadModels", "selectionChanged"]);

const openPlayer = ref(false);
const openProperties = ref(false);
const openDelete = ref(false);
const openDuplicate = ref(false);
const selectedStatechart = ref<RecentFile>(null);
const editDialogMode = ref<"create" | "edit" | "info">(null);
const duplicateId = ref<string>("");

const activeModelId = computed(() => editorStateStore().getModelId);

const selectedIds = ref<Set<string>>(new Set());
const pendingBatchDeleteIds = ref<string[]>([]);

watch(
  () => compProps.statecharts,
  (statecharts) => {
    const visibleIds = new Set(statecharts.map((stc) => stc.modelId));
    const next = new Set([...selectedIds.value].filter((id) => visibleIds.has(id)));
    if (next.size !== selectedIds.value.size) {
      selectedIds.value = next;
      emit("selectionChanged", next.size);
    }
  }
);

function toggleSelection(modelId: string) {
  if (modelId === activeModelId.value) return;
  const next = new Set(selectedIds.value);
  if (next.has(modelId)) {
    next.delete(modelId);
  } else {
    next.add(modelId);
  }
  selectedIds.value = next;
  emit("selectionChanged", next.size);
}

function clearSelection() {
  selectedIds.value = new Set();
  emit("selectionChanged", 0);
}

function selectAll() {
  const next = new Set(compProps.statecharts.map((stc) => stc.modelId).filter((id) => id !== activeModelId.value));
  selectedIds.value = next;
  emit("selectionChanged", next.size);
}

defineExpose({ selectedCount: () => selectedIds.value.size, initBatchDelete, downloadSelected, clearSelection, selectAll });

async function downloadSelected() {
  const zip = new JSZip();
  const selected = compProps.statecharts.filter((stc) => selectedIds.value.has(stc.modelId));

  await Promise.allSettled(
    selected.map(async (stc) => {
      try {
        const content = await cloudStatechartService.getModel(stc.modelId, false);
        const baseName = stc.model.title || stc.modelId;
        const fileName = (zip.file(baseName + ".scm") ? `${baseName}_${stc.modelId}` : baseName) + ".scm";
        zip.file(fileName, typeof content === "string" ? content : JSON.stringify(content));
      } catch {
        // skip models that can't be fetched
      }
    })
  );

  const blob = await zip.generateAsync({ type: "blob" });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = "statecharts.zip";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

async function initBatchDelete() {
  const ids = [...selectedIds.value];
  const first = compProps.statecharts.find((stc) => stc.modelId === ids[0]);
  if (!first) return;
  selectedStatechart.value = first;
  pendingBatchDeleteIds.value = ids.slice(1);
  openDelete.value = true;
}

const statechartsWithNew = computed(() => {
  const tempStatecharts: Array<RecentFile | NewStatechart> = [...compProps.statecharts];
  if (compProps.withNew) {
    tempStatecharts.unshift(newStatechart);
  }
  return tempStatecharts;
});

const hoverStates = ref(new Array(statechartsWithNew.value.length).fill(false));

interface menuAction {
  title: string;
  action: (statechartId: string, openAfter?: boolean) => void;
  icon: string;
  testId: string;
}

function menuActionsFor(modelId: string): menuAction[] {
  return [
    ...(modelId !== activeModelId.value
      ? [
          {
            title: "delete",
            action: initDeleteDialog,
            icon: "mdi-delete-forever",
            testId: "statechartCardsGroup.delete-list-item",
          } as menuAction,
        ]
      : []),
    {
      title: "duplicate",
      action: initDuplicate,
      icon: "mdi-content-copy",
      testId: "statechartCardsGroup.duplicate-list-item",
    },
    {
      title: "publish player",
      action: (_statechartId: string) => {
        openPlayer.value = true;
      },
      icon: "mdi-share-variant",
      testId: "statechartCardsGroup.publish-list-item",
    },
    {
      title: "show/edit details",
      action: initEditDialog,
      icon: "mdi-card-text-outline",
      testId: "statechartCardsGroup.edit-list-item",
    },
  ];
}

function playerState(statechart: RecentFile): "outdated" | "up-to-date" | null {
  if (statechart.model.players?.length > 0) {
    if (statechart.model.modifiedAt > statechart.model.players[0].creationDate) {
      return "outdated";
    } else {
      return "up-to-date";
    }
  }
  return null;
}

function openStatechart(statechart: RecentFile) {
  emit("openStatechart", statechart);
}

function initDuplicate(_statechartId: string, openAfter = false) {
  editDialogMode.value = "create";
  openDuplicate.value = openAfter;
  openProperties.value = true;
}

function initEditDialog(_statechartId: string) {
  editDialogMode.value = "edit";
  openProperties.value = true;
}

function initDeleteDialog(_statechartId: string) {
  openDelete.value = true;
}

async function statechartDeleted() {
  openDelete.value = false;
  if (pendingBatchDeleteIds.value.length > 0) {
    const remaining = pendingBatchDeleteIds.value;
    pendingBatchDeleteIds.value = [];
    selectedIds.value = new Set();
    emit("selectionChanged", 0);
    await Promise.allSettled(remaining.map((id) => cloudStatechartService.deleteModel(id)));
  }
  emit("update:reloadModels");
}

async function duplicateStatechart(newTitle: string) {
  const statechartId: string = selectedStatechart.value.modelId;
  openProperties.value = false;
  await cloudStatechartService.duplicateModel(statechartId, newTitle).then((id) => {
    if (openDuplicate.value) {
      duplicateId.value = id;
      WizardEventBus.on("initialised", openOnInit);
    }
    emit("update:reloadModels");
    if (!openDuplicate.value) {
      MessageEventBus.emit("success", {
        text: "Statechart got duplicated.",
        id: "statechart-wizard",
      });
    }
  });

  openDuplicate.value = false;
}

function openOnInit() {
  WizardEventBus.off("initialised", openOnInit);
  nextTick(() => {
    const statechart = compProps.statecharts.find((el: RecentFile) => {
      return el.modelId === duplicateId.value;
    });
    if (statechart) {
      emit("openStatechart", statechart);
    } else {
      MessageEventBus.emit("error", {
        text: "Statechart could not be opened.",
        id: "statechart-wizard",
      });
    }
  });
}

async function saveEdit(newInfo: { newTitle?: string; newDescription?: string }) {
  const statechartId: string = selectedStatechart.value.modelId;
  if (editorStateStore().getModelId === statechartId) {
    WizardEventBus.emit("forceSelectingStatechart");
  }

  await cloudStatechartService.updateInfo(statechartId, newInfo.newDescription, newInfo.newTitle).then(() => {
    if (editorStateStore().getModelId === statechartId) {
      WizardEventBus.emit("forceSelectingStatechart");
    }
    emit("update:reloadModels");
    MessageEventBus.emit("success", {
      text: "Changes got saved.",
      id: "statechart-wizard",
    });
  });

  openProperties.value = false;
}
</script>

<style scoped>
.statechartImg {
  background: rgba(var(--v-theme-white));
}
.cards-group-container {
  gap: 15px;
  max-width: none;
  overflow-y: auto;
  padding: 1px 0;
}
.selection-bar {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
